import { useEffect, useState, useRef } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { SessionService } from "../services/session-service"
import { ByodJoinPage } from "./html-pages"
import { SessionModel } from "../models/session"
import { SessionDeviceModel } from "../models/session-device"
import { SpeakerModel } from "../models/speaker"
import { ApiService } from "../services/api-service"
import { scopedFetch } from "../services/scoped-fetch"
import fixWebmDuration from "fix-webm-duration"
import io from 'socket.io-client';

/*
BYOD Connection Order

1. VerifyInputAndAudio
2. RequestAccessKey
3. ConnectToProcessors
4. DetermineSpeakers*
5. requestStartToProcessing
6. Media Socket Worklets
*/

function JoinPage() {
    // Audio connection data
    const audiows = useRef(null)
    const [videows, setVideoWs] = useState(null)
    const [audioconnected, setAudioConnected] = useState(false)
    const [videoconnected, setVideoConnected] = useState(false)
    const [authenticated, setAuthenticated] = useState(false)
    const [streamReference, setStreamReference] = useState(null)
    const [audioContext, setAudioContext] = useState(null)
    const [mediaRecorder, setMediaRecorder] = useState(null)
    const [processor, setProcessor] = useState(null)
    const [audioSenderProcessor, setAudioSenderProcessor] = useState(null)
    const [source, setSource] = useState(null)
    const [ending, setEnding] = useState({ value: false })
    const [reconnectCounter, setReconnectCounter] = useState(0)
    // Audio-processor WS auto-reconnect plumbing. WS drops are common on
    // classroom WiFi; the backend rejoin path already supports re-attaching
    // to the same session_device, so a transient blip should be silent for
    // the student instead of dead-ending at "Connection lost".
    const reconnectAttemptRef = useRef(0)
    const reconnectTimerRef = useRef(null)
    const [reconnecting, setReconnecting] = useState(false)
    const RECONNECT_MAX = 3
    const RECONNECT_DELAYS_MS = [1500, 3000, 6000]
    // Surfaced when a student's own voiceprint can't be pushed into the
    // audio_processor at join time. Renders as a blocking overlay with a
    // "Back to home" action so the user has an obvious path to recovery.
    const [voiceprintLoadError, setVoiceprintLoadError] = useState(null)
    const [showAlert, setShowAlert] = useState(false)
    const [alertMessage, setAlertMessage] = useState("")
    const [frameBuffer, setFrameBuffer] = useState([]); // Buffer for cartoonized frames
    const [frameBufferLength, setFrameBufferLength] = useState(0);
    const [cartoonImgUrl, setCartoonImgUrl] = useState("");
    const [cartoonImgBatch, setCartoonImgBatch] = useState(1);
    const [renderingStarted, setRenderingStarted] = useState(false)
    const cartoonIntervalRef = useRef(null)  // Tracked so it can be cleared on unmount.
    const socketRef = useRef(null);

    // Session data
    const [sessionDevice, setSessionDevice] = useState(null)
    const [session, setSession] = useState(null)
    const [key, setKey] = useState(null)

    const sessionService = new SessionService()
    const apiService = new ApiService()

    const [transcripts, setTranscripts] = useState([])
    const [startTime, setStartTime] = useState()
    const [endTime, setEndTime] = useState()
    const [displayTranscripts, setDisplayTranscripts] = useState([])
    const [currentTranscript, setCurrentTranscript] = useState({})
    const [timeRange, setTimeRange] = useState([0, 1])
    const [selectedSpkrId1, setSelectedSpkrId1] = useState(-1)
    const [selectedSpkrId2, setSelectedSpkrId2] = useState(-1)
    const [spkr1Transcripts, setSpkr1Transcripts] = useState([])
    const [spkr2Transcripts, setSpkr2Transcripts] = useState([])
    const [details, setDetails] = useState("Group")

    const [currentForm, setCurrentForm] = useState("")
    const [displayText, setDisplayText] = useState("")
    const [reload, setReload] = useState(false)
    const [pageTitle, setPageTitle] = useState("Join Discussion")
    const [sessionClosing, setSessionClosing] = useState(false)
    const [prevSessionId, setPrevSessionId] = useState(-1)
    const [prevSessionDeviceId, setPrevSessionDeviceId] = useState(-1)

    const [name, setName] = useState("")
    const [pcode, setPcode] = useState("")
    const [wrongInput, setWrongInput] = useState(false)
    const [joinwith, setJoinwith] = useState("")
    const [preview, setPreview] = useState(false)
    const [previewLabel, setPreviewLabel] = useState("Turn On Preview")
    const navigate = useNavigate()

    const [showFeatures, setShowFeatures] = useState([])
    const [showBoxes, setShowBoxes] = useState([])

    const [numSpeakers, setNumSpeakers] = useState()
    const [speakers, setSpeakers] = useState([])
    const [speakersValidated, setSpeakersValidated] = useState(false)
    // Default to automatic diarization — zero setup friction. Users who
    // want voice fingerprint enrollment can opt in, and only then do we
    // ask for the number of collaborators (which is meaningless under
    // automatic diarization, where Deepgram detects speakers unbounded).
    const [speakerMode, setSpeakerMode] = useState("auto")
    const [selectedSpeaker, setSelectedSpeaker] = useState(null)
    const [currBlob, setCurrBlob] = useState(null)
    const [invalidName, setInvalidName] = useState(false)
    const [constraintObj, setConstraintObj] = useState(null)
    const [facilitationEnabled, setFacilitationEnabled] = useState(true)
    // Per-group code for viewer devices (teammates joining without audio)
    // and live count of viewers currently connected.
    const [viewerCode, setViewerCode] = useState(null)
    const [viewerCount, setViewerCount] = useState(1)
    // CS330 classroom: track current user for the student auto-enrollment path.
    // Null for anonymous BYOD (existing behavior unchanged).
    const [currentUser, setCurrentUser] = useState(null)
    const autoPushedSpeakerIds = useRef(new Set())
    // CS330 classroom: drop mic chunks while a fingerprint binary is in flight,
    // so the audio_processing server reads the fingerprint cleanly without an
    // interleaved mic chunk overwriting it.
    const pauseMicForFingerprint = useRef(false)

    const POD_COLOR = "#FF6655"
    const GLOW_COLOR = "#ffc3bd"
    const interval = 10000

    let wakeLock = null

    // CS330 classroom: detect logged-in user once at mount. Anonymous BYOD
    // gets currentUser=null and follows the unchanged path. When a student
    // is detected, we force Mode B (enrolled) and turn on the facilitator —
    // students arriving via the StudentRightPanel join path should never
    // make these choices manually (a wrong dropdown would silently kill
    // voiceprint matching → "Speaker N" tags instead of display_name).
    useEffect(() => {
        let cancelled = false
        fetch('/api/v1/me')
            .then(r => (r.ok ? r.json() : null))
            .then(u => {
                if (cancelled) return
                setCurrentUser(u)
                if (u && u.role === 'student') {
                    setSpeakerMode('enrolled')
                    setFacilitationEnabled(true)
                    if (!numSpeakers) setNumSpeakers(1)
                    // Pre-fill display_name into the recorder name field if
                    // empty so the student doesn't have to retype it. The
                    // URL ?name= takes precedence (the recorder student
                    // typed the GROUP name there, not their own name).
                    if (u.display_name) {
                        setName(prev => prev || u.display_name)
                    }
                }
            })
            .catch(() => {})
        return () => { cancelled = true }
    }, [])

    // CS330 classroom: auto-submit the join form for logged-in students
    // arriving with both URL params (passcode + group name). The legacy
    // form has a speaker-mode dropdown and a facilitator checkbox that are
    // pure footguns for students — leaving the dropdown on the default
    // "Automatic" disables voiceprint matching entirely. Skipping the form
    // forecloses that whole class of misconfiguration. The browser's mic
    // permission prompt is unavoidable, but that's the only interaction.
    const autoJoinedRef = useRef(false)
    useEffect(() => {
        if (autoJoinedRef.current) return
        if (!currentUser || currentUser.role !== 'student') return
        if (!pcode || !name) return
        // Speaker mode is set to 'enrolled' in the previous effect; gate on
        // it here too so we don't auto-submit before that flush has run.
        if (speakerMode !== 'enrolled') return
        autoJoinedRef.current = true
        setCurrentForm("Connecting")
        // Pass 'enrolled' explicitly so verifyInputAndAudio's `selectedSpeakerMode
        // || "auto"` fallback can't downgrade us, even if speakerMode state
        // hadn't reached the verify call yet.
        //
        // collaborators=0 is intentional: cs330 doesn't use pre-allocated
        // blank Speaker slots. The recorder student gets a user_id-bound
        // Speaker via ensure_student_speaker on the server, and late-joiners
        // get added via the student_participant_joined SocketIO event. Any
        // non-zero count would create blank rows that the auto-push useEffect
        // can't fingerprint (no user_id to look up a voiceprint), failing
        // the `speakers.every(s => s.fingerprinted)` gate and showing the
        // legacy "Missing Speaker Fingerprints" error.
        verifyInputAndAudio(name, pcode, 'Audio', 0, 'enrolled')
    }, [currentUser, pcode, name, speakerMode])

    // CS330 classroom: read passcode + name when StudentRightPanel redirects
    // here. Prefer sessionStorage (clean URL), fall back to URL params for
    // backwards compatibility / shareable links. The keys cs330_pending_*
    // are consumed once and cleared so a stale value can't trigger an
    // unintended auto-join on a future visit.
    const [searchParams] = useSearchParams()
    useEffect(() => {
        let urlPasscode = searchParams.get('passcode')
        let urlName = searchParams.get('name')
        if (!urlPasscode && !urlName) {
            try {
                urlPasscode = sessionStorage.getItem('cs330_pending_passcode')
                urlName = sessionStorage.getItem('cs330_pending_name')
                if (urlPasscode || urlName) {
                    sessionStorage.removeItem('cs330_pending_passcode')
                    sessionStorage.removeItem('cs330_pending_name')
                }
            } catch (_) {}
        }
        if (urlPasscode) {
            setPcode(urlPasscode)
            setTimeout(() => {
                const el = document.getElementById('passcode')
                if (el) el.value = urlPasscode
            }, 100)
        }
        if (urlName) {
            setName(urlName)
            setTimeout(() => {
                const el = document.getElementById('name')
                if (el) el.value = urlName
            }, 100)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // CS330 classroom: when a logged-in recorder joins a Mode B session and
    // there are user_id-bound speakers in the list (the recorder's own
    // student record from byod_join, late-joiners via student_participant_joined,
    // or pre-selections via preload_participant), auto-push their persisted
    // voiceprints into audio_processing via the existing speaker-message
    // protocol. Works for both student and admin recorders — the targets
    // filter on `s.user_id != null` is sufficient guard. Anonymous joins
    // (no currentUser) do nothing; speakers without user_id (legacy
    // collaborator slots) are ignored.
    useEffect(() => {
        if (!currentUser) return
        if (speakerMode !== 'enrolled') return
        if (!sessionDevice?.id) return
        if (!audiows.current || audiows.current.readyState !== 1 /* OPEN */) return
        if (!Array.isArray(speakers) || speakers.length === 0) return

        const targets = speakers.filter(
            s => s.user_id != null && !autoPushedSpeakerIds.current.has(s.id)
        )
        if (targets.length === 0) return

        let cancelled = false
        // Single retry after 1s on network failure or 5xx. Handles transient
        // hiccups during a discussion without losing voiceprint matching for
        // a late-joiner.
        const fetchWithRetry = async (url, retries = 1) => {
            for (let attempt = 0; attempt <= retries; attempt++) {
                try {
                    const res = await fetch(url)
                    if (res.ok) return res
                    if (res.status >= 500 && attempt < retries) {
                        await new Promise(r => setTimeout(r, 1000))
                        continue
                    }
                    return res
                } catch (err) {
                    if (attempt < retries) {
                        await new Promise(r => setTimeout(r, 1000))
                        continue
                    }
                    throw err
                }
            }
        }
        ;(async () => {
            try {
                const res = await fetchWithRetry(`/api/v1/sessions/${sessionDevice.id}/student_voiceprints`)
                if (!res || !res.ok) return
                const data = await res.json()
                if (cancelled) return
                const vpMap = new Map((data.voiceprints || []).map(v => [v.speaker_id, v]))
                const updatedIds = []
                // Track per-target outcome so we can surface a precise error
                // if the recorder student's own voiceprint was missing — that
                // case otherwise looks identical to "everything succeeded but
                // they're stuck waiting" from the UI's perspective.
                const ownTargetIds = targets
                    .filter(sp => sp.user_id === currentUser?.id)
                    .map(sp => sp.id)
                const ownPushed = new Set()
                // Pause mic chunk forwarding during the fingerprint sends so
                // audio_processing reads each fingerprint binary as the next
                // chunk after its JSON header without an interleaved mic chunk.
                pauseMicForFingerprint.current = true
                try {
                    for (const sp of targets) {
                        // Bail out cleanly if the WS dropped or this effect
                        // was cancelled by a state change. Without this, a
                        // mid-loop reconnect would interleave fingerprint
                        // bytes from the dead push into the new WS.
                        if (cancelled) break
                        if (!audiows.current || audiows.current.readyState !== 1) break
                        const vp = vpMap.get(sp.id)
                        if (!vp) continue
                        try {
                            // base64 → Uint8Array → Int16Array → Float32 (audio_processing
                            // expects pcm_f32le since the BYOD start config sets that
                            // encoding; convert here so the existing reformat_data path
                            // applies the same int16 conversion as for live mic audio).
                            const binStr = atob(vp.audio_b64)
                            const bytes = new Uint8Array(binStr.length)
                            for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i)
                            const int16 = new Int16Array(bytes.buffer)
                            const f32 = new Float32Array(int16.length)
                            for (let i = 0; i < int16.length; i++) f32[i] = int16[i] / 32768.0

                            const message = {
                                type: 'speaker',
                                id: sp.id,
                                alias: sp.alias || vp.alias || '',
                                size: f32.byteLength,
                                blob_type: 'pcm_f32le',
                            }
                            audiows.current.send(JSON.stringify(message))
                            audiows.current.send(f32.buffer)
                            autoPushedSpeakerIds.current.add(sp.id)
                            updatedIds.push(sp.id)
                            if (ownTargetIds.includes(sp.id)) ownPushed.add(sp.id)
                        } catch (err) {
                            console.warn('[CS330 auto-enroll] push failed for speaker', sp.id, err)
                        }
                    }
                } finally {
                    // Brief delay before resuming mic to let the fingerprint
                    // bytes drain through the WS pipeline.
                    setTimeout(() => { pauseMicForFingerprint.current = false }, 150)
                }
                if (updatedIds.length > 0 && !cancelled) {
                    setSpeakers(prev => prev.map(s =>
                        updatedIds.includes(s.id) ? { ...s, fingerprinted: true } : s
                    ))
                }
                // Hard-fail surface for a recorder student whose OWN voiceprint
                // wasn't loaded. Without this, the auto-validate effect never
                // flips speakersValidated and the user sits on a meaningless
                // "Setting up your voice…" screen until they reload.
                if (!cancelled
                        && currentUser?.role === 'student'
                        && ownTargetIds.length > 0
                        && ownTargetIds.every(id => !ownPushed.has(id))) {
                    setVoiceprintLoadError(
                        "Your voiceprint couldn't be loaded into the discussion. "
                        + "Go back to your home screen, re-record your voiceprint, then rejoin."
                    )
                }
            } catch (err) {
                console.warn('[CS330 auto-enroll] fetch failed:', err)
                if (currentUser?.role === 'student') {
                    setVoiceprintLoadError(
                        "Couldn't load your voiceprint (network error). "
                        + "Reload the page and try again, or go back to your home screen."
                    )
                }
            }
        })()
        return () => { cancelled = true }
        // audioconnected is in deps so a WS reconnect (false→true) re-fires
        // this effect; combined with autoPushedSpeakerIds.current.clear() in
        // attemptReconnect, all voiceprints get re-pushed to the new
        // audio_processor instance.
    }, [currentUser, speakers, sessionDevice?.id, speakerMode, audioconnected])

    // CS330: auto-validate speakers once every user_id-bound Speaker has been
    // fingerprinted by the auto-push above. Without this, students would land
    // on the legacy "Confirm Speakers" enrollment screen — the very step the
    // auto-submit was supposed to skip. Late-joiners arrive via
    // student_participant_joined and re-enter this effect; we re-validate
    // automatically once their voiceprint pushes through.
    useEffect(() => {
        if (!currentUser || currentUser.role !== 'student') return
        if (speakerMode !== 'enrolled') return
        if (speakersValidated) return
        if (!Array.isArray(speakers) || speakers.length === 0) return
        // Only consider user-bound speakers — legacy unbound slots would
        // (incorrectly) gate validation forever, but for student joins
        // collaborators=0 means there shouldn't be any.
        const bound = speakers.filter(s => s && s.user_id != null)
        if (bound.length === 0) return
        if (bound.every(s => s.fingerprinted)) {
            setSpeakersValidated(true)
        }
    }, [currentUser, speakers, speakerMode, speakersValidated])

    // Join the group room once we know the session_device_id, so the
    // recorder receives group_viewer_count, speaker_renamed, group_ended
    // and session_finalized events just like viewers do.
    useEffect(() => {
        if (!socketRef.current || !sessionDevice?.id) return;
        const socket = socketRef.current;
        const sdId = sessionDevice.id;

        const onConnect = () => {
            socket.emit('join_group_room', { session_device_id: sdId });
        };
        if (socket.connected) onConnect();
        socket.on('connect', onConnect);

        const onViewerCount = (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id === sdId) setViewerCount(data.count || 1);
            } catch (e) {}
        };
        const onSpeakerRenamed = (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id !== sdId) return;
                // Update speakers state + any transcript speaker_tag for this speaker id.
                setSpeakers(prev => (Array.isArray(prev) ? prev : []).map(s =>
                    s && s.id === data.speaker_id ? { ...s, alias: data.alias } : s
                ));
                setTranscripts(prev => (Array.isArray(prev) ? prev : []).map(t =>
                    t && t.speaker_id === data.speaker_id ? { ...t, speaker_tag: data.alias } : t
                ));
            } catch (e) {}
        };

        // If the TA ends the session globally while we're still recording,
        // the server broadcasts group_ended to our group's room. We must
        // mirror the local end-recording behavior so the audio WS is closed
        // and the UI transitions to ClosedSession → PostDiscussionArtifacts.
        // Without this handler the recorder would silently keep "recording"
        // against a dead Redis key while their audio goes nowhere.
        const onGroupEnded = (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id !== sdId) return;
                // Only act if we haven't already ended ourselves (avoids
                // double-disconnect and a flash of error messages when
                // the recorder clicks End and the server echoes back).
                if (ending.value) return;
                try { disconnect(true); } catch (e) {}
                enterClosedSession("The session has been ended.");
            } catch (e) {}
        };

        // CS330 classroom: a student deleted their voiceprint or withdrew
        // consent. Forward a removal message to audio_processing so the
        // in-memory fingerprint dict is updated; future utterances from
        // that voice will no longer match.
        const onStudentVoiceprintRemoved = (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id !== sdId) return;
                if (audiows.current && audiows.current.readyState === 1 /* OPEN */) {
                    audiows.current.send(JSON.stringify({
                        type: 'speaker_remove',
                        id: data.speaker_id,
                    }));
                }
                // Also clear the local autoPushed cache so a re-record can
                // re-arm the same Speaker without being suppressed.
                autoPushedSpeakerIds.current.delete(data.speaker_id);
            } catch (e) {}
        };

        // CS330 classroom: a student or admin re-recorded the voiceprint
        // mid-session. The companion 'student_voiceprint_removed' event
        // normally clears autoPushedSpeakerIds first; clear it here too in
        // case events arrive out of order — idempotent when REMOVED already
        // ran. Then bump speakers state so the auto-push useEffect re-runs
        // and pushes the new bytes.
        const onStudentVoiceprintReplaced = (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id !== sdId) return;
                autoPushedSpeakerIds.current.delete(data.speaker_id);
                setSpeakers(prev => (Array.isArray(prev) ? prev : []).map(s =>
                    s && s.id === data.speaker_id
                        ? { ...s, fingerprinted: false, alias: data.alias || s.alias }
                        : s
                ));
            } catch (e) {}
        };

        // CS330 classroom: late-joining student viewer. Server has already
        // upserted session_participant + ensure_student_speaker; we just need
        // to add the new Speaker to local state so the auto-push useEffect
        // picks it up on its next run and forwards the voiceprint to
        // audio_processing's hot-add path.
        const onStudentParticipantJoined = (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id !== sdId) return;
                setSpeakers(prev => {
                    const arr = Array.isArray(prev) ? prev : [];
                    if (arr.some(s => s.id === data.speaker_id)) return arr;  // already known
                    return [...arr, {
                        id: data.speaker_id,
                        session_device_id: sdId,
                        alias: data.alias || '',
                        user_id: data.user_id,
                        binding_method: 'pre_enrolled',
                        fingerprinted: false,
                    }];
                });
            } catch (e) {}
        };

        socket.on('group_viewer_count', onViewerCount);
        socket.on('speaker_renamed', onSpeakerRenamed);
        socket.on('group_ended', onGroupEnded);
        socket.on('student_participant_joined', onStudentParticipantJoined);
        socket.on('student_voiceprint_removed', onStudentVoiceprintRemoved);
        socket.on('student_voiceprint_replaced', onStudentVoiceprintReplaced);

        return () => {
            socket.off('connect', onConnect);
            socket.off('group_viewer_count', onViewerCount);
            socket.off('speaker_renamed', onSpeakerRenamed);
            socket.off('group_ended', onGroupEnded);
            socket.off('student_participant_joined', onStudentParticipantJoined);
            socket.off('student_voiceprint_removed', onStudentVoiceprintRemoved);
            socket.off('student_voiceprint_replaced', onStudentVoiceprintReplaced);
            // Deliberately do NOT emit leave_group_room here. Ending
            // recording calls disconnect(true), which clears sessionDevice
            // and triggers this cleanup — but the recorder is transitioning
            // into the post-recording edit view (PostDiscussionArtifacts),
            // which still needs to receive speaker_renamed and
            // session_finalized events from the same room. Leaving the
            // room on cleanup would cut off those broadcasts. The room
            // membership is released naturally when the socket disconnects
            // (page navigation or close).
        };
    }, [sessionDevice?.id]);

    useEffect(() => {
        socketRef.current = io();

        // Cleanup on page unload — covers BOTH normal navigation AND
        // browser bfcache (back button). beforeunload does NOT fire for
        // bfcache; pagehide does. Without this handler, the prior page's
        // audio WebSocket and SocketIO connection stay alive on the server
        // until ping_timeout fires, leaking eventlet greenlets.
        const handlePageHide = () => {
            try {
                if (audiows.current) {
                    try { audiows.current.close(); } catch (e) {}
                    audiows.current = null;
                }
                if (socketRef.current) {
                    try { socketRef.current.disconnect(); } catch (e) {}
                }
            } catch (e) {
                console.warn('pagehide cleanup error', e);
            }
        };
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('beforeunload', handlePageHide);

        // Restore BYOD session data from sessionStorage on page refresh
        const savedKey = sessionStorage.getItem('byod_processing_key');
        const savedSessionId = sessionStorage.getItem('byod_session_id');
        const savedDeviceId = sessionStorage.getItem('byod_device_id');

        if (savedKey && savedSessionId && savedDeviceId) {
            console.log('Restoring BYOD session from sessionStorage');
            // Restore the session data
            setKey(savedKey);

            const parsedSessionId = parseInt(savedSessionId, 10);
            const parsedDeviceId = parseInt(savedDeviceId, 10);

            // Check if the user was in post-discussion mode before reload.
            // If so, restore that state directly so they see their artifacts
            // instead of the join form.
            const wasClosed = sessionStorage.getItem('byod_closed_session');
            const savedPrevSdId = parseInt(sessionStorage.getItem('byod_prev_sdid') || '0', 10);
            const savedPrevSId = parseInt(sessionStorage.getItem('byod_prev_sid') || '0', 10);
            if (wasClosed && savedPrevSdId > 0) {
                setPrevSessionDeviceId(savedPrevSdId);
                setPrevSessionId(savedPrevSId || parsedSessionId);
                setCurrentForm('ClosedSession');
                // Don't restore audio state — session is already ended.
                // Post-discussion scoped fetches still work because the
                // processing_key in sessionStorage is intact.
            }

            // Fetch session and device data from API
            sessionService.getSession(parsedSessionId).then(
                (response) => {
                    if (response.status === 200) {
                        response.json().then((jsonObj) => {
                            setSession(SessionModel.fromJson(jsonObj));
                        });
                    }
                },
                (error) => console.error('Failed to restore session:', error)
            );

            sessionService.getSessionDevice(parsedSessionId, parsedDeviceId).then(
                (response) => {
                    if (response.status === 200) {
                        response.json().then((jsonObj) => {
                            setSessionDevice(SessionDeviceModel.fromJson(jsonObj));
                            // Restore the group's viewer code too so the
                            // group-info bar can display it after reload.
                            if (jsonObj && jsonObj.viewer_code) {
                                setViewerCode(jsonObj.viewer_code);
                            }
                        });
                    }
                },
                (error) => console.error('Failed to restore session device:', error)
            );

            // Pre-fill name and passcode for easier reconnection if user
            // is on the join form (not ClosedSession). Both fields are
            // uncontrolled DOM inputs, so we set them imperatively after
            // React renders. No existing render logic is modified.
            const savedName = sessionStorage.getItem('byod_name');
            const savedPcode = sessionStorage.getItem('byod_pcode');
            if (!wasClosed) {
                if (savedName) setName(savedName);
                if (savedPcode) setPcode(savedPcode);
                setTimeout(() => {
                    if (savedName) {
                        const nameEl = document.getElementById('name');
                        if (nameEl) nameEl.value = savedName;
                    }
                    if (savedPcode) {
                        const pcodeEl = document.getElementById('passcode');
                        if (pcodeEl) pcodeEl.value = savedPcode;
                    }
                }, 100);
            }
        }

        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('beforeunload', handlePageHide);
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
            // Cancel any pending reconnect attempt so a delayed timer doesn't
            // try to open a new WS after the component is gone.
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        // initialize the options toolbar
        let featuresArr = [
            "Emotional tone",
            "Analytic thinking",
            "Clout",
            "Authenticity",
            "Certainty",
            "Participation",
            "Social Impact",
            "Responsivity",
            "Internal Cohesion",
            "Newness",
            "Communication Density",
        ]
        initChecklistData(featuresArr, setShowFeatures)
        // initialize the components toolbar
        let boxArr = [
            "Timeline control",
            "Discussion timeline",
            "Discussion features",
            "Radar chart",
            "Participation",
            "Social Impact",
            "Responsivity",
            "Internal Cohesion",
            "Newness",
            "Communication Density",
        ]
        initChecklistData(boxArr, setShowBoxes)
    }, [])

    // Render the next batch of cartoon frames whenever the buffer grows.
    // Single useEffect (was previously duplicated three times in this file).
    useEffect(() => {
        renderFrameFromBuffer()
    }, [frameBufferLength])

    // Clean up the cartoon interval when the component unmounts so it
    // doesn't keep ticking against a torn-down setState.
    useEffect(() => {
        return () => {
            if (cartoonIntervalRef.current) {
                clearInterval(cartoonIntervalRef.current)
                cartoonIntervalRef.current = null
            }
        }
    }, [])

    useEffect(() => {
        if (constraintObj && pcode !== "" && joinwith !== "") handleStream()
    }, [constraintObj, pcode, joinwith])
/*
    useEffect(() => {
        if (
            audiows.current != null
        ) {
            console.log("called connect_audio_processor_service")
            connect_audio_processor_service()
        }
    }, [audiows.current])
*/
    useEffect(() => {
        if (videows != null) {
            console.log("called connect_video_processor_service")
            connect_video_processor_service()
        }
    }, [videows])

    //Use effect to intermetently reload the transcript
    useEffect(() => {
        let intervalLoad
        // fetch the transcript
        if (session !== null && sessionDevice !== null) {
            fetchTranscript(sessionDevice.id)

            intervalLoad = setInterval(() => {
                fetchTranscript(sessionDevice.id)
            }, 2000)
        }

        return () => {
            clearInterval(intervalLoad)
        }
    }, [session, sessionDevice])

    useEffect(() => {
        if (session !== null && speakersValidated) {
            const sessionLen =
                Object.keys(session).length > 0 ? session.length : 0
            const sTime = Math.round(sessionLen * timeRange[0] * 100) / 100
            const eTime = Math.round(sessionLen * timeRange[1] * 100) / 100
            setStartTime(sTime)
            setEndTime(eTime)
            generateDisplayTranscripts(sTime, eTime)
        }
    }, [transcripts, startTime, endTime, session, speakersValidated, timeRange])

    useEffect(() => {
        if (displayTranscripts) {
            console.log("reloaded page - displayTranscripts")
            setSpeakerTranscripts()
        }
    }, [displayTranscripts, selectedSpkrId1, selectedSpkrId2, details])

    //Use effect to start audio and video processing
    useEffect(() => {
        if (audioconnected && !videoconnected) {
            requestStartAudioProcessing()
        }
        if (audioconnected && videoconnected) {
            requestStartVideoProcessing()
        }
    }, [audioconnected, videoconnected])

    //Use effect to start the camera and microphone for video and audio capturing
    useEffect(() => {
        if (authenticated) {
            const loadWorklet = async () => {
                await audioContext.audioWorklet.addModule(
                    "audio-sender-processor.js",
                )
                const workletProcessor = new AudioWorkletNode(
                    audioContext,
                    "audio-sender-processor",
                )
                workletProcessor.port.onmessage = (data) => {
                    // CS330: drop mic chunks while a fingerprint upload is in
                    // flight so audio_processing reads the fingerprint binary
                    // cleanly. The pause is ~few hundred ms; lost mic frames
                    // don't materially affect ASR.
                    if (pauseMicForFingerprint.current) return
                    // Drop chunks during the WS close→reopen reconnect window.
                    // Sending to a closed WS would throw; a few hundred ms of
                    // silence is preferable to a crash mid-discussion.
                    if (!audiows.current || audiows.current.readyState !== 1) return
                    audiows.current.send(data.data.buffer)
                }
                source
                    .connect(workletProcessor)
                    .connect(audioContext.destination)
                setAudioSenderProcessor(workletProcessor)
            }

            const videoPlay = () => {
                let video = document.querySelector("video")
                video.srcObject = streamReference
                video.onloadedmetadata = function (ev) {
                    video.play()
                    mediaRecorder.start(interval)
                }

                mediaRecorder.ondataavailable = async function (ev) {
                    const bufferdata = await ev.data.arrayBuffer()
                    fixWebmDuration(
                        ev.data,
                        interval * 6 * 60 * 24,
                        (fixedblob) => {
                            videows.send(fixedblob)
                            audiows.current.send(fixedblob)
                        },
                    )
                }
            }

            if (authenticated && joinwith === "Audio") {
                loadWorklet().catch(console.error)
            } else if (
                authenticated &&
                (joinwith === "Video" || joinwith === "Videocartoonify")
            ) {
                videoPlay()
            }
        }
    }, [authenticated])

    //Use effect to toggle video view pane
    useEffect(() => {
        if (preview) {
            setPreviewLabel("Turn Off Preview")
        } else {
            setPreviewLabel("Turn On Preview")
        }
    }, [preview])

    const openForms = (form, speaker = null) => {
        setCurrentForm(form)
        if (form === "fingerprintAudio" || form === "renameAlias") {
            setSelectedSpeaker(speaker)
        }
    }

    const closeForm = () => {
        setCurrentForm("")
    }

    const enterClosedSession = (text) => {
        setDisplayText(text)
        setCurrentForm('ClosedSession')
        try {
            const sdId = sessionDevice?.id || prevSessionDeviceId
            const sId = session?.id || prevSessionId
            if (sdId > 0) sessionStorage.setItem('byod_prev_sdid', String(sdId))
            if (sId > 0) sessionStorage.setItem('byod_prev_sid', String(sId))
            sessionStorage.setItem('byod_closed_session', '1')
        } catch (e) { /* ignore */ }
    }

    // End recording for THIS group only (not the whole session).
    // Stops audio capture, broadcasts group_ended to viewers, and
    // transitions our own UI into the post-session review state where
    // speakers can still be edited before the recorder clicks Finalize.
    const endRecording = async () => {
        const sdId = sessionDevice?.id
        if (!sdId) return
        try {
            await scopedFetch(`/api/v1/groups/${sdId}/end_recording`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            })
        } catch (e) {
            console.warn('end_recording failed', e)
        }
        // Close the local audio path. This preserves sessionStorage so the
        // post-discussion view can still authenticate its fetches.
        try { disconnect(true) } catch (e) {}
        // The server already broadcasts group_ended to viewers; for this
        // recorder we jump straight to the ClosedSession form which
        // renders PostDiscussionArtifacts.
        enterClosedSession("Your group's recording has ended.")
    }

    // Disconnects from websocket server and audio stream.
    const disconnect = (permanent = false) => {
        console.log("disconnect called", permanent)
        if(ending.value)
            return
        if (permanent) {
            setPageTitle("Join Discussion")
            setName("")
            setPcode("")
            ending.value = true
            setSpeakersValidated(false)
            setSpeakers(null)
            setPrevSessionId(session?.id || prevSessionId)
            setPrevSessionDeviceId(sessionDevice?.id || prevSessionDeviceId)
            setSession(null)
            setSessionDevice(null)
            setKey(null)

            // NOTE: We deliberately do NOT clear sessionStorage here.
            // The post-discussion artifacts view (PostDiscussionArtifacts)
            // still needs the processing key in sessionStorage so its
            // scoped-key fetches (transcript, speakers, facilitation
            // messages) can authenticate. Keys are cleared when the user
            // closes that dialog — see closeDialog().
        }

        if (wakeLock) releaseWakeLock()

        if (source != null) {
            source.disconnect()
            setSource(null)
        }
        if (audioContext != null) {
            setAudioSenderProcessor(null)
        }
        if (mediaRecorder != null) {
            mediaRecorder.stop()
            setMediaRecorder(null)
        }

        if (streamReference != null) {
            streamReference.getAudioTracks().forEach((track) => track.stop())
            setStreamReference(null)
        }

        setAudioConnected(false)
        setVideoConnected(false)
        setAuthenticated(false)

        if (audiows.current != null) {
            audiows.current.close();
            audiows.current = null;
        }
        if (videows != null) {
            videows.close()
            setVideoWs(null)
        }
    }

    const confirmSpeakers = () => {
        console.log(speakers)
        if (speakers.every((s) => s.fingerprinted)) {
            let message = null
            message = {
                type: "speaker",
                id: "done",
                speakers: speakers,
            }
            audiows.current.send(JSON.stringify(message))
            setSpeakersValidated(true)
        } else {
            setDisplayText(
                "Not all added speakers have a fingerprint. Please record one for each speaker",
            )
            setCurrentForm("FingerprintingError")
        }
    }

    const saveAudioFingerprint = (audioblob) => {
        //store blob for confirmation
        setCurrBlob(audioblob)
    }

    const addSpeakerFingerprint = async () => {
        if (audiows.current === null) {
            console.error("[Fingerprint] WebSocket is null, cannot send fingerprint")
            return
        }

        try {
            let message = {
                type: "speaker",
                id: selectedSpeaker.id,
                alias: selectedSpeaker.alias,
                size: currBlob.size,
                blob_type: currBlob.type,
            }

            console.log("[Fingerprint] Converting blob to array buffer...")
            let data = await currBlob.arrayBuffer()
            console.log("[Fingerprint] Blob size:", currBlob.size, "ArrayBuffer size:", data.byteLength)

            console.log("[Fingerprint] Decoding audio data...")
            let audiodata = await audioContext.decodeAudioData(data)
            console.log("[Fingerprint] Audio decoded - channels:", audiodata.numberOfChannels,
                        "length:", audiodata.length, "sampleRate:", audiodata.sampleRate)

            const updatedSpeakers = speakers.map((s) =>
                s.id === selectedSpeaker.id ? { ...s, fingerprinted: true } : s,
            )
            setSpeakers(updatedSpeakers)

            // Get the raw Float32Array data and convert to ArrayBuffer for sending
            const channelData = audiodata.getChannelData(0)
            console.log("[Fingerprint] Channel data length:", channelData.length)

            // Send the JSON message first
            console.log("[Fingerprint] Sending JSON message...")
            audiows.current.send(JSON.stringify(message))

            // Send the audio data as ArrayBuffer (using .buffer property of the Float32Array)
            console.log("[Fingerprint] Sending audio data as ArrayBuffer...")
            audiows.current.send(channelData.buffer)

            console.log("[Fingerprint] Successfully sent speaker fingerprint")
            setCurrBlob(null)
            closeDialog()
        } catch (error) {
            console.error("[Fingerprint] Error in addSpeakerFingerprint:", error)
            setDisplayText("Error recording fingerprint: " + error.message)
            setCurrentForm("FingerprintingError")
        }
    }

    const changeAliasName = (newAlias) => {
        if (newAlias === "") {
            setInvalidName(true)
            return
        }
        console.log(
            `Speaker ID: ${selectedSpeaker.id} with new alias: ${newAlias}`,
        )
        setInvalidName(false)
        const speakerId = selectedSpeaker.id
        const fetchData = new SessionService().updateCollaborator(
            speakerId,
            newAlias,
        )
        fetchData
            .then(
                (response) => {
                    if (response.status === 200) {
                        response.json().then((jsonObj) => {
                            console.log(jsonObj)
                            const speaker = SpeakerModel.fromJson(jsonObj)
                            console.log(speaker)
                            const updatedSpeakers = speakers.map((s) =>
                                s.id === selectedSpeaker.id
                                    ? { ...s, alias: speaker.alias }
                                    : s,
                            )
                            setSpeakers(updatedSpeakers)
                            setSelectedSpeaker(null)
                        })
                    } else {
                        setShowAlert(true)
                        setAlertMessage(response.json()["message"])
                    }
                },
                (apierror) => {
                    console.log(
                        "byod-join-components func: changealiasname 1 ",
                        apierror,
                    )
                },
            )
            .finally(() => {
                closeDialog()
            })
    }

    const handleStream = async () => {
        try {
            //Await wake lock for screen first
            await acquireWakeLock()
            //handle older browsers that might implement getUserMedia in some way

            if (navigator.mediaDevices === undefined) {
                navigator.mediaDevices = {}
                navigator.mediaDevices.getUserMedia = function (constraintObj) {
                    let getUserMedia =
                        navigator.webkitGetUserMedia ||
                        navigator.mozGetUserMedia
                    if (!getUserMedia) {
                        return Promise.reject(
                            new Error(
                                "getUserMedia is not implemented in this browser",
                            ),
                        )
                    }
                    return new Promise(function (resolve, reject) {
                        getUserMedia.call(
                            navigator,
                            constraintObj,
                            resolve,
                            reject,
                        )
                    })
                }
            } else {
                navigator.mediaDevices
                    .enumerateDevices()
                    .then((devices) => {
                        devices.forEach((device) => {
                            //console.log(device.kind.toUpperCase(), device.label);
                            //, device.deviceId
                        })
                    })
                    .catch((err) => {
                        console.log(err.name, err.message)
                    })
            }

            if (navigator.mediaDevices != null) {
                const stream =
                    await navigator.mediaDevices.getUserMedia(constraintObj)
                // media.then(function (stream) {
                setStreamReference(stream)
                if (joinwith === "Audio") {
                    const context = new AudioContext({ sampleRate: 16000 })
                    setSource(context.createMediaStreamSource(stream))
                    setAudioContext(context)
                    console.log("connect to websocket");
                    audiows.current = new WebSocket(
                        apiService.getAudioWebsocketEndpoint(),
                    )
                    connect_audio_processor_service();
                    
                } else if (
                    joinwith === "Video" ||
                    joinwith === "Videocartoonify"
                ) {
                    var opt
                    if (
                        MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
                    ) {
                        opt = { mimeType: "video/webm; codecs=vp9,opus" }
                    } else if (
                        MediaRecorder.isTypeSupported("video/webm;codecs=vp8")
                    ) {
                        opt = { mimeType: "video/webm; codecs=vp8,opus" }
                    } else {
                        opt = { mimeType: "video/webm" }
                    }

                    const mediaRec = new MediaRecorder(stream, opt)
                    setMediaRecorder(mediaRec)
                }
            } else {
                setDisplayText("No media devices detected.")
                setCurrentForm("JoinError")
                disconnect(true)
            }
        } catch (ex) {
            console.log(ex)
            setDisplayText("Failed to get user audio source.")
            setCurrentForm("JoinError")
            disconnect(true)
        }
    }

    // Verifies the users connection input and that the user
    // has a microphone accessible to the browser.
    const verifyInputAndAudio = (names, passcode, joinswith, collaborators, selectedSpeakerMode) => {
        if (names === null) {
            names = "User Device"
        }
        setName(names)
        setJoinwith(joinswith)
        setNumSpeakers(collaborators)
        const mode = selectedSpeakerMode || "auto"
        setSpeakerMode(mode)
        requestAccessKey(names, passcode, collaborators, joinswith, mode)
    }

    // Requests session access from the server.
    const requestAccessKey = (names, passcode, collaborators, joinwith, mode) => {
        ending.value = false
        setCurrentForm("Connecting")
        const constraint = {}
        if (joinwith === "Video" || joinwith === "Videocartoonify") {
            constraint.audio = true
            constraint.video = {
                facingMode: "user",
                width: 150, //{ min: 640, ideal: 1280, max: 1920 },
                height: 80, //{ min: 480, ideal: 720, max: 1080 }
            }
        } else {
            constraint.audio = true
            constraint.video = false
        }
        // Mode A (auto diarization): don't pre-create Speaker rows — they're created
        // dynamically as Deepgram identifies speakers during the discussion
        const joinCollaborators = mode === "auto" ? 0 : collaborators;
        sessionService.joinByodSession(names, passcode, joinCollaborators).then(
            (response) => {
                if (response.status === 200) {
                    response.json().then((jsonObj) => {
                        setSession(SessionModel.fromJson(jsonObj["session"]))
                        setSessionDevice(
                            SessionDeviceModel.fromJson(
                                jsonObj["session_device"],
                            ),
                        )
                        setSpeakers(
                            SpeakerModel.fromJsonList(jsonObj["speakers"]),
                        )
                        setKey(jsonObj.key);
                        // Capture the per-group viewer code so we can show it
                        // to the recorder and tell them "your teammates should
                        // type this on /viewer".
                        if (jsonObj["session_device"] && jsonObj["session_device"].viewer_code) {
                            setViewerCode(jsonObj["session_device"].viewer_code)
                        }

                        // Persist BYOD session data to sessionStorage for refresh recovery
                        sessionStorage.setItem('byod_processing_key', jsonObj.key);
                        sessionStorage.setItem('byod_session_id', jsonObj["session"].id.toString());
                        sessionStorage.setItem('byod_device_id', jsonObj["session_device"].id.toString());
                        sessionStorage.setItem('byod_name', names);
                        sessionStorage.setItem('byod_pcode', passcode);
                        // Clear stale ClosedSession flag on fresh join
                        sessionStorage.removeItem('byod_closed_session');
                        sessionStorage.removeItem('byod_prev_sdid');
                        sessionStorage.removeItem('byod_prev_sid');

                        setConstraintObj(constraint)
                        setPcode(passcode)
                        setJoinwith(joinwith)

                        // Mode A (auto): bypass fingerprint enrollment
                        if (mode === "auto") {
                            setSpeakersValidated(true)
                        }

                        // Start facilitation agent for this device if toggle is on.
                        // The server requires either a TA login OR a scoped
                        // X-Processing-Key matching the session_device_id —
                        // sessionStorage.byod_processing_key is set just above
                        // by the join handler, and scopedFetch reads it.
                        if (facilitationEnabled) {
                            const sdId = jsonObj["session_device"].id;
                            // Make sure the key is in sessionStorage BEFORE the fetch
                            // so scopedFetch can attach it.
                            try {
                                if (jsonObj["key"]) {
                                    sessionStorage.setItem('byod_processing_key', jsonObj["key"]);
                                }
                            } catch (e) {}
                            scopedFetch(`/api/facilitation/start`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ session_device_id: sdId }),
                            }).catch(() => {});
                        }

                        //activate video websocket also if user joins with video
                        if (
                            joinwith === "Video" ||
                            joinwith === "Videocartoonify"
                        ) {
                            setVideoWs(
                                new WebSocket(
                                    apiService.getVideoWebsocketEndpoint(),
                                ),
                            )
                        }
                    })
                } else if (response.status === 400 || response.status === 401) {
                    response.json().then((jsonObj) => {
                        setDisplayText(jsonObj["message"])
                        setCurrentForm("JoinError")
                        disconnect(true)
                    })
                }
            },
            (apierror) => {
                setDisplayText("Contact Administrator")
                setCurrentForm("JoinError")
                disconnect(true)
                console.log(
                    "byod-join-component error func : requestAccessKey 1",
                    apierror,
                )
            },
        )
    }

    const closeAlert = () => {
        setShowAlert(false)
    }

    // Open a fresh audio_processing WS using the existing key/session and
    // rewire all three handlers. Used for both the initial connection and
    // each reconnect attempt — keeps the wiring in one place so onclose
    // behavior stays consistent across attempts.
    const attemptReconnect = () => {
        if (ending.value) {
            setReconnecting(false)
            return
        }
        try {
            // Detach handlers from the dead WS before reassigning the ref.
            // Without this, a late `onclose` from the old instance can fire
            // after we've moved on, kicking off a parallel reconnect chain
            // (or worse, double-incrementing the attempt counter so we hit
            // RECONNECT_MAX in half the calendar time).
            if (audiows.current) {
                try {
                    audiows.current.onopen = null
                    audiows.current.onmessage = null
                    audiows.current.onclose = null
                    audiows.current.onerror = null
                } catch (_) {}
            }
            // Clear so the auto-push useEffect re-pushes every voiceprint
            // into the fresh audio_processor instance (its in-memory speaker
            // dict is empty after the WS drop).
            autoPushedSpeakerIds.current.clear()
            audiows.current = new WebSocket(apiService.getAudioWebsocketEndpoint())
            connect_audio_processor_service()
        } catch (err) {
            console.error('[Reconnect] failed to open WS', err)
            // Schedule next attempt or fall through; the new WS's onclose
            // will fire if construction succeeded but connection failed.
            if (reconnectAttemptRef.current >= RECONNECT_MAX) {
                setReconnecting(false)
                disconnect(true)
                enterClosedSession("Connection to the session has been lost.")
            }
        }
    }

    // Connects to audio processor websocket server.
    const connect_audio_processor_service = () => {
        audiows.current.binaryType = "arraybuffer"

        audiows.current.onopen = (e) => {
            console.log("[Connected audio processor service]")
            console.log("speakers ", speakers)
            // Reset reconnect bookkeeping on every successful open. Both the
            // initial join and a successful reconnect land here, so a future
            // drop starts a fresh retry budget instead of inheriting old
            // attempt counters.
            reconnectAttemptRef.current = 0
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current)
                reconnectTimerRef.current = null
            }
            setReconnecting(false)
            setAudioConnected(true)
            setReconnectCounter(0)
            setPageTitle(name)
            setReload(true)
            setCurrentForm("")
        };

        audiows.current.onmessage = (e) => {
            const message = JSON.parse(e.data)
            setAuthenticated(true)
            if (message["type"] === "start") {
                closeDialog()
            } else if (message["type"] === "error") {
                disconnect(true)
                enterClosedSession(
                    "The connection to the session has been closed by the server.",
                )
            } else if (message["type"] === "end") {
                disconnect(true)
                enterClosedSession("The session has been closed by the owner.")
            }
        }

        audiows.current.onerror = (e) => {
            // Plain WS errors don't always close the socket; logging them
            // makes silent stalls debuggable. The browser fires onclose
            // afterward in most cases, which then drives the reconnect
            // logic — so no state mutation needed here.
            console.warn('[audio_processing WS] error event', e)
        }

        audiows.current.onclose = (e) => {
            console.log("[Disconnected]", ending.value)
            if (ending.value) {
                console.log("ending ...")
                return
            }
            // Unexpected close. Keep the mic + AudioContext alive across the
            // reconnect window — the worklet's onmessage will resume sending
            // to the new audiows.current as soon as it opens (the readyState
            // guard added below drops chunks during the gap).
            setAudioConnected(false)
            if (reconnectAttemptRef.current < RECONNECT_MAX) {
                const delay = RECONNECT_DELAYS_MS[reconnectAttemptRef.current]
                reconnectAttemptRef.current += 1
                setReconnecting(true)
                if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
                reconnectTimerRef.current = setTimeout(attemptReconnect, delay)
            } else {
                // Out of retries — fall through to the existing dead-end
                // path so the user can claim transcripts / view artifacts.
                setReconnecting(false)
                disconnect(true)
                enterClosedSession("Connection to the session has been lost.")
            }
        }
    }

    // Connects to video processor websocket server.
    const connect_video_processor_service = () => {
        videows.binaryType = "arraybuffer"

        videows.onopen = (e) => {
            console.log("[Connected to video processor services]")
            setVideoConnected(true)
        }

        videows.onmessage = (e) => {
            if (typeof e.data === 'string'){
                const message = JSON.parse(e.data);
                if (message['type'] === 'start') {
                    setAuthenticated(true);
                    closeDialog();
                }else if(message['type'] === 'attention_data'){

                }else if (message['type'] === 'error') {
                    disconnect(true);
                    setDisplayText('The connection to the session has been closed by the server.');
                    setCurrentForm('ClosedSession');
                } else if (message['type'] === 'end') {
                    disconnect(true);
                    setDisplayText('The session has been closed by the owner.');
                    setCurrentForm('ClosedSession');
                }
            }else if (e.data instanceof Blob){
                const url = URL.createObjectURL(e.data);
                // Add the processed frame to the buffer
                
                setFrameBuffer(prevBuffer =>{
                        const newItems = [...prevBuffer, url]
                        if (newItems.length % 40 == 0) {
                            setFrameBufferLength(newItems.length)
                            // setCartoonImgBatch(prevCount => prevCount + 1)
                            // setFrameBufferLength(prevCount => prevCount + 1)
                        }
                        
                        return newItems
                    } 
                );

                setRenderingStarted(true)
            }
        };

        videows.onclose = e => {
            console.log('[Disconnected]',ending.value);
        };
    }

    // Begin capturing and sending client audio.
    const requestStartAudioProcessing = () => {
        console.log("Starting Audio")
        let message = null
        if (audiows.current === null) {
            return
        }
        if (joinwith === "Audio") {
            message = {
                type: "start",
                key: key,
                start_time: 0.0,
                sample_rate: audioContext.sampleRate,
                encoding: "pcm_f32le",
                channels: 1,
                streamdata: "audio",
                tag: speakerMode === "enrolled",
                embeddings_file: sessionDevice.embeddings,
                deviceid: sessionDevice.id,
                sessionid: session.id,
                numSpeakers: speakerMode === "enrolled" ? numSpeakers : 0,
                speaker_mode: speakerMode,
                speaker_ids: speakers ? speakers.map(s => s.id) : [],
            }
        } else if (joinwith === "Video" || joinwith === "Videocartoonify") {
            message = {
                type: "start",
                key: key,
                start_time: 0.0,
                sample_rate: 16000,
                encoding: "pcm_f16le",
                video_encoding: "video/mp4",
                channels: 2,
                streamdata: "video",
                embeddings_file: sessionDevice.embeddings,
                deviceid: sessionDevice.id,
                sessionid: session.id,
                numSpeakers: speakerMode === "enrolled" ? numSpeakers : 0,
                speaker_mode: speakerMode,
                speaker_ids: speakers ? speakers.map(s => s.id) : [],
            }
        }
        audiows.current.send(JSON.stringify(message))
    }

    // Begin capturing and sending client video.
    const requestStartVideoProcessing = () => {
        let message = null
        if (videows === null) {
            return
        }
        if (joinwith === "Video") {
            message = {
                type: "start",
                key: key,
                start_time: 0.0,
                sample_rate: 16000,
                encoding: "pcm_f16le",
                video_encoding: "video/mp4",
                channels: 2,
                streamdata: "video",
                tag: speakerMode === "enrolled",
                embeddings_file: sessionDevice.embeddings,
                deviceid: sessionDevice.id,
                sessionid: session.id,
                numSpeakers: speakerMode === "enrolled" ? numSpeakers : 0,
                speaker_mode: speakerMode,
                speaker_ids: speakers ? speakers.map(s => s.id) : [],
            }
        } else if (joinwith === "Videocartoonify") {
            message = {
                type: "start",
                key: key,
                start_time: 0.0,
                sample_rate: 16000,
                encoding: "pcm_f16le",
                video_encoding: "video/mp4",
                channels: 2,
                streamdata: "video",
                tag: speakerMode === "enrolled",
                embeddings_file: sessionDevice.embeddings,
                deviceid: sessionDevice.id,
                sessionid: session.id,
                numSpeakers: speakerMode === "enrolled" ? numSpeakers : 0,
                speaker_mode: speakerMode,
                speaker_ids: speakers ? speakers.map(s => s.id) : [],
                Video_cartoonify: true,
            }
        }

        videows.send(JSON.stringify(message))
    }

    const requestHelp = () => {
        sessionDevice.button_pressed = !sessionDevice.button_pressed
        sessionService.setDeviceButton(
            sessionDevice.id,
            sessionDevice.button_pressed,
            key,
        )
    }

    const navigateToLogin = (confirmed = false) => {
        if (!confirmed && (audioconnected || videoconnected)) {
            setCurrentForm("NavGuard")
        } else {
            disconnect(true)
            setCurrentForm("")
            return navigate("/")
        }
    }

    const fetchTranscript = async (deviceid) => {
        try {
            const response =
                await sessionService.getSessionDeviceTranscriptsForClient(
                    deviceid,
                )

            if (response.status === 200) {
                const jsonObj = await response.json()
                const fetched_transcripts = jsonObj.sort((a, b) =>
                    a.start_time > b.start_time ? 1 : -1,
                )

                // Previously fan-out: N parallel GET /transcripts/:id/speaker_metrics
                // calls (one per transcript) every 2s. speaker_metrics is a
                // stub that always returns zeros, so this was pure waste —
                // and the fan-out amplified every slow backend query by N×,
                // helping push the eventlet hub into a hang under flaky
                // classroom network conditions. Drop the metrics layer; the
                // live recording UI doesn't render per-utterance metrics.
                setTranscripts(fetched_transcripts)
                const sessionLen =
                    Object.keys(session).length > 0 ? session.length : 0
                setStartTime(Math.round(sessionLen * timeRange[0] * 100) / 100)
                setEndTime(Math.round(sessionLen * timeRange[1] * 100) / 100)
            } else if (response.status === 400 || response.status === 401) {
                console.log(response, "no transcript obj")
            }
        } catch (error) {
            console.log(
                "byod-join-component error func : fetch transcript",
                error,
            )
        }
    }

    // Walk through the next 40 cartoon frames at 500ms each.
    //
    // The previous implementation created 40 setIntervals in a for-loop,
    // each capturing the loop variable `i` via closure (so they all read
    // the same final value), AND never cleared any of them — so each call
    // permanently leaked 40 intervals firing forever. This version uses
    // exactly one tracked interval that advances an index and clears
    // itself when the batch is done.
    const renderFrameFromBuffer = () => {
        if (frameBufferLength <= (40 * cartoonImgBatch)) return
        if (cartoonIntervalRef.current) {
            clearInterval(cartoonIntervalRef.current)
            cartoonIntervalRef.current = null
        }
        let idx = (cartoonImgBatch - 1) * 40
        const end = cartoonImgBatch * 40
        cartoonIntervalRef.current = setInterval(() => {
            if (idx >= end) {
                clearInterval(cartoonIntervalRef.current)
                cartoonIntervalRef.current = null
                return
            }
            setCartoonImgUrl(frameBuffer[idx])
            idx++
        }, 500)
        setCartoonImgBatch(prevCount => prevCount + 1)
    }

    const ResetTimeRange = (values) => {
        if (session !== null) {
            const sessionLen =
                Object.keys(session).length > 0 ? session.length : 0
            setTimeRange(values)
            const start = Math.round(sessionLen * values[0] * 100) / 100
            const end = Math.round(sessionLen * values[1] * 100) / 100
            setStartTime(start)
            setEndTime(end)
            generateDisplayTranscripts(start, end)
        }
    }

    const generateDisplayTranscripts = (s, e) => {
        setDisplayTranscripts(
            transcripts.filter((t) => t.start_time >= s && t.start_time <= e),
        )
    }

    const setSpeakerTranscripts = () => {
        if (displayTranscripts.length) {
            setSpkr1Transcripts(
                displayTranscripts.reduce((values, transcript) => {
                    if (
                        selectedSpkrId1 === -1 ||
                        transcript.speaker_id === selectedSpkrId1
                    )
                        values.push(transcript)
                    return values
                }, []),
            )
            setSpkr2Transcripts(
                displayTranscripts.reduce((values, transcript) => {
                    if (
                        selectedSpkrId2 === -1 ||
                        transcript.speaker_id === selectedSpkrId2
                    )
                        values.push(transcript)
                    return values
                }, []),
            )
        } else {
            setSpkr1Transcripts([])
            setSpkr2Transcripts([])
        }
    }

    const seeAllTranscripts = () => {
        if (Object.keys(currentTranscript) > 0 && sessionDevice !== null) {
            setCurrentForm("gottoselectedtranscript")
        } else if (sessionDevice !== null) {
            setCurrentForm("gototranscript")
        }
    }

    const loading = () => {
        return session === null || transcripts === null
    }

    const onClickedTimeline = (transcript) => {
        setCurrentForm("Transcript")
        setCurrentTranscript(transcript)
    }

    const onClickedKeyword = (transcript) => {
        setCurrentTranscript(transcript)
        setCurrentForm("gottoselectedtranscript")
    }

    const openDialog = (form) => {
        setCurrentForm(form)
    }

    const closeDialog = () => {
        if(currentForm === "ClosedSession") {
            setPrevSessionId(-1)
            setPrevSessionDeviceId(-1)
            // Now that the post-session artifact view is closing, it's safe
            // to drop the BYOD processing key — no more scoped-key fetches.
            try {
                sessionStorage.removeItem('byod_processing_key');
                sessionStorage.removeItem('byod_session_id');
                sessionStorage.removeItem('byod_device_id');
                sessionStorage.removeItem('byod_closed_session');
                sessionStorage.removeItem('byod_prev_sdid');
                sessionStorage.removeItem('byod_prev_sid');
                sessionStorage.removeItem('byod_name');
                sessionStorage.removeItem('byod_pcode');
            } catch (e) { /* ignore */ }
        }
        setCurrentForm("")
    }

    const changeTouppercase = (e) => {
        let val = e.target.value.toUpperCase()
        if (val.length <= 4) {
            setWrongInput(false)
        } else {
            setWrongInput(true)
        }
        setPcode(val)
    }

    const togglePreview = () => {
        setCurrentForm("")
        setPreview(!preview)
    }

    const onSessionClosing = (isClosing) => {
        setSessionClosing(isClosing)
    }

    const acquireWakeLock = async () => {
        if (!("wakeLock" in navigator)) {
            console.error(
                "Screen Wake Lock API is not supported by the browser",
            )
            return
        }

        try {
            wakeLock = await navigator.wakeLock.request("screen")
            console.log("Wake lock is activated.")
            wakeLock.addEventListener("release", () => {
                console.log("Wake Lock has been released")
            })
            document.addEventListener("visibilitychange", async () => {
                if (
                    wakeLock !== null &&
                    document.visibilityState === "visible"
                ) {
                    wakeLock = await navigator.wakeLock.request("screen")
                }
            })
        } catch (err) {
            console.log(err)
        }
    }

    const releaseWakeLock = async () => {
        try {
            wakeLock.release().then(() => {
                wakeLock = null
            })
        } catch (err) {
            console.log(`WakeLock release error: ${err}`)
        }
    }

    const initChecklistData = (featuresArr, setFn) => {
        let valueInd = 0
        let showFeats = []
        for (const feature of featuresArr) {
            showFeats.push({ label: feature, value: valueInd, clicked: true })
            valueInd++
        }
        setFn(showFeats)
    }

    const viewComparison = () => {
        setDetails("Comparison")
    }

    const viewIndividual = () => {
        setDetails("Individual")
    }

    const viewGroup = () => {
        setDetails("Group")
    }

    const sessionDevBtnPressed =
        sessionDevice !== null ? sessionDevice.button_pressed : null

    return (
        <>
        {/* CS330: optional pre-selection panel — appears once a Mode-B session
            is joined, lets the TA recorder load teammates' voiceprints upfront.
            Doesn't replace the late-joiner SocketIO path; just an early-bind
            shortcut for when the whole team is present at start.
            Hidden for student-role users: students join through the auto-push
            path with no participant selection — showing this panel contradicts
            the "no manual setup" contract for the student fast-path. */}
        {sessionDevice?.id && speakerMode === 'enrolled' && !speakersValidated
            && currentUser?.role !== 'student' && (
            <PreloadVoiceprintsPanel
                sessionDeviceId={sessionDevice.id}
                currentUserId={currentUser?.id}
                speakers={speakers}
            />
        )}
        <ByodJoinPage
            // Students follow the auto-submit / auto-validate fast path —
            // no join form, no speaker enrollment screen, no fingerprint
            // dialogs. ByodJoinPage gates the legacy UI on this flag.
            isStudentRecorder={currentUser?.role === 'student'}
            connected={audioconnected}
            authenticated={authenticated}
            GLOW_COLOR={GLOW_COLOR}
            POD_COLOR={POD_COLOR}
            button_pressed={sessionDevBtnPressed}
            verifyInputAndAudio={verifyInputAndAudio}
            closeDialog={closeDialog}
            currentForm={currentForm}
            displayText={displayText}
            navigate={navigate}
            navigateToLogin={navigateToLogin}
            pageTitle={pageTitle}
            requestHelp={requestHelp}
            pcode={pcode}
            setPcode={setPcode}
            wrongInput={wrongInput}
            changeTouppercase={changeTouppercase}
            joinwith={joinwith}
            preview={preview}
            previewLabel={previewLabel}
            togglePreview={togglePreview}
            disconnect={disconnect}
            sessionDevice={sessionDevice}
            setRange={ResetTimeRange}
            onClickedTimeline={onClickedTimeline}
            onClickedKeyword={onClickedKeyword}
            session={session}
            displayTranscripts={displayTranscripts}
            startTime={startTime}
            endTime={endTime}
            transcripts={transcripts}
            loading={loading}
            onSessionClosing={onSessionClosing}
            currentTranscript={currentTranscript}
            seeAllTranscripts={seeAllTranscripts}
            openDialog={openDialog}
            setCurrentForm={setCurrentForm}
            showBoxes={showBoxes}
            showFeatures={showFeatures}
            videoApiEndpoint={apiService.getVideoServerEndpoint()}
            setSpeakersValidated={setSpeakersValidated}
            speakersValidated={speakersValidated}
            speakers={speakers}
            authKey={key}
            openForms={openForms}
            closeForm={closeForm}
            selectedSpkrId1={selectedSpkrId1}
            setSelectedSpkrId1={setSelectedSpkrId1}
            selectedSpkrId2={selectedSpkrId2}
            setSelectedSpkrId2={setSelectedSpkrId2}
            spkr1Transcripts={spkr1Transcripts}
            spkr2Transcripts={spkr2Transcripts}
            selectedSpeaker={selectedSpeaker}
            setSelectedSpeaker={setSelectedSpeaker}
            saveAudioFingerprint={saveAudioFingerprint}
            addSpeakerFingerprint={addSpeakerFingerprint}
            confirmSpeakers={confirmSpeakers}
            closeAlert={closeAlert}
            changeAliasName={changeAliasName}
            details={details}
            setDetails={setDetails}
            viewIndividual={viewIndividual}
            viewComparison={viewComparison}
            viewGroup={viewGroup}
            cartoonImgUrl = {cartoonImgUrl}
            invalidName={invalidName}
            prevSessionId={prevSessionId}
            prevSessionDeviceId={prevSessionDeviceId}
            socket={socketRef.current}
            speakerMode={speakerMode}
            setSpeakerMode={setSpeakerMode}
            facilitationEnabled={facilitationEnabled}
            setFacilitationEnabled={setFacilitationEnabled}
            viewerCode={viewerCode}
            viewerCount={viewerCount}
            endRecording={endRecording}
        />
        {/* Voiceprint load failure — blocking modal for student recorders
            whose own voiceprint couldn't be pushed at join time. We render
            this BEFORE the reconnect overlay so it stays visible across a
            transient WS drop too. */}
        {voiceprintLoadError && (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.55)', zIndex: 1100,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <div style={{
                    background: '#fff', padding: '24px 28px', borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)', maxWidth: 420, textAlign: 'left',
                }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#b91c1c' }}>
                        Voiceprint not loaded
                    </div>
                    <div style={{ fontSize: 13, color: '#374151', marginTop: 8, lineHeight: 1.5 }}>
                        {voiceprintLoadError}
                    </div>
                    <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            onClick={() => navigate('/student-app')}
                            style={{
                                padding: '8px 16px', borderRadius: 6, border: 'none',
                                background: '#2563eb', color: '#fff', fontSize: 14, cursor: 'pointer',
                            }}
                        >
                            Back to home
                        </button>
                    </div>
                </div>
            </div>
        )}
        {/* Reconnecting overlay — shown while the audio_processing WS is
            in the gap between unexpected close and a successful retry. The
            mic + AudioContext stay alive underneath; the discussion resumes
            silently once the new WS opens. */}
        {reconnecting && (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.45)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <div style={{
                    background: '#fff', padding: '22px 28px', borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)', maxWidth: 360, textAlign: 'center',
                }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>Reconnecting…</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6, lineHeight: 1.45 }}>
                        Trying to restore the connection. The discussion will resume automatically.
                    </div>
                </div>
            </div>
        )}
        </>
    )
}


// CS330 classroom: floating panel rendered above the BYOD form. Lets the
// recorder pick teammates from the student roster and load their persistent
// voiceprints upfront. Each selection POSTs to /preload_participant which
// emits the same student_participant_joined event as a real late-viewer join,
// so the existing onStudentParticipantJoined + auto-push useEffect drive
// the actual fingerprint upload to audio_processing.
function PreloadVoiceprintsPanel({ sessionDeviceId, currentUserId, speakers }) {
    const [students, setStudents] = useState([])
    const [selected, setSelected] = useState(new Set())
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState(null)
    const [done, setDone] = useState(new Set())

    useEffect(() => {
        let cancelled = false
        fetch('/api/v1/students').then(r => r.ok ? r.json() : []).then(rows => {
            if (cancelled) return
            // Show only students with voiceprints (others can't be pre-loaded
            // without an enrollment recording). Hide the recorder themselves
            // since their voiceprint is auto-pushed by the existing useEffect.
            setStudents(rows.filter(r => r.has_voiceprint && r.id !== currentUserId))
        }).catch(() => {})
        return () => { cancelled = true }
    }, [currentUserId])

    // Mark students whose Speaker rows are already in the session as 'done',
    // so we don't ask the user to preload someone who's already armed.
    useEffect(() => {
        const armedUserIds = new Set((speakers || []).map(s => s.user_id).filter(x => x))
        if (armedUserIds.size > 0) {
            setDone(prev => {
                const next = new Set(prev)
                for (const uid of armedUserIds) next.add(uid)
                return next
            })
        }
    }, [speakers])

    const toggle = (uid) => {
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(uid)) next.delete(uid); else next.add(uid)
            return next
        })
    }

    const submit = async () => {
        setBusy(true); setError(null)
        const ids = Array.from(selected)
        const newlyDone = []
        for (const uid of ids) {
            try {
                const res = await fetch(`/api/v1/sessions/${sessionDeviceId}/preload_participant`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user_id: uid }),
                })
                if (res.ok) newlyDone.push(uid)
            } catch (e) {}
        }
        setDone(prev => { const n = new Set(prev); newlyDone.forEach(x => n.add(x)); return n })
        setSelected(new Set())
        setBusy(false)
        if (newlyDone.length === 0) setError('No participants were loaded — check the network or try again.')
    }

    if (students.length === 0) return null

    return (
        <div style={{
            position: 'fixed', top: 16, right: 16, width: 320,
            background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
            padding: 14, boxShadow: '0 6px 16px rgba(0,0,0,0.08)', zIndex: 50,
            fontFamily: 'system-ui, sans-serif', fontSize: 13,
        }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Pre-load Teammates' Voiceprints</div>
            <div style={{ color: '#6b7280', marginBottom: 8 }}>
                Optional. Select students who are already in the room so their voices
                are recognized from the first utterance.
            </div>
            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #f3f4f6', borderRadius: 6, padding: 4 }}>
                {students.map(s => {
                    const isDone = done.has(s.id)
                    return (
                        <label key={s.id} style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px',
                            opacity: isDone ? 0.6 : 1,
                            cursor: isDone ? 'default' : 'pointer',
                        }}>
                            <input
                                type="checkbox"
                                disabled={isDone}
                                checked={selected.has(s.id)}
                                onChange={() => toggle(s.id)}
                            />
                            <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{s.email}</span>
                            <span style={{ color: '#6b7280' }}>{s.display_name || ''}</span>
                            {isDone && <span style={{ marginLeft: 'auto', color: '#16a34a' }}>● armed</span>}
                        </label>
                    )
                })}
            </div>
            {error && <div style={{ color: '#b91c1c', marginTop: 6 }}>{error}</div>}
            <button
                disabled={busy || selected.size === 0}
                onClick={submit}
                style={{
                    marginTop: 10, width: '100%',
                    padding: '8px 12px', borderRadius: 6, border: 'none',
                    background: selected.size === 0 ? '#9ca3af' : '#2563eb', color: '#fff',
                    cursor: busy || selected.size === 0 ? 'not-allowed' : 'pointer', fontSize: 13,
                }}
            >
                {busy ? 'Loading…' : `Pre-load ${selected.size} selected`}
            </button>
        </div>
    )
}


export { JoinPage }
